// PicksPage — the platform's affiliate shelf ("Worth a look" in the customer app).
//
// Platform admin only. Every value on screen is a field platform_picks() returned
// (sql/079); nothing here reads a table directly and nothing here is computed, because
// the two halves of this shelf — what a customer sees, and what the platform earns —
// are both things an operator can only get wrong once.
//
// THE TWO GATES THIS PAGE EXISTS TO EXPLAIN. customer_shop() renders no affiliate shelf
// unless BOTH hold, and sql/079 puts them on one line:
//
//     IF v_legal IS NULL OR NOT COALESCE(v_allow_shop, false) THEN
//       v_picks := '[]'::jsonb;
//
//   1. The platform's legal entity name is set. FTC 16 CFR 255 requires the disclosure to
//      NAME the company earning the commission; with no name there is no disclosure, so
//      the app answers with an absent shelf rather than an undisclosed one. That gate is
//      global, this page can read it back (legal_name), and an unset name is therefore not
//      a warning here — it is the headline.
//   2. The individual operator's `allow_shop` is true. It is `NOT NULL DEFAULT false`
//      (sql/071_requests_catalog.sql:37), so out of the box it is off for EVERY operator.
//
// platform_picks() does not return anything about gate 2 — no operator list, no count of
// who has it switched on. So this page says the gate exists and refuses to imply a number
// it was never given. "Switched on" here means switched on at the platform level, and that
// is the only claim any label on this screen makes.
//
// Two things deliberately absent, and they are absent everywhere including the code:
//   • NO PRICE FIELD. Prices belong to the retailer, change without telling us, and go
//     stale inside a day. An affiliate card carrying our stale price is a compliance
//     problem (a price claim we cannot substantiate) and a broken promise to the
//     customer who taps it and finds a different number. The disclosure line already
//     says prices are the retailer's; the card says nothing about money at all.
//     product_picks has no price column either (sql/079), so there is nothing to bind to.
//   • NO COMMISSION RATE, no bounty, no earnings estimate. Chewy's terms have never been
//     read from inside the program — the figures in circulation came from third-party
//     pages. An unverified rate shown to the owner becomes a number he plans around, so
//     it is not displayed, not stored and not implied.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

const HJ = () => ({
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${getAuthToken()}`,
  "Content-Type": "application/json",
});
const rpc = async (fn, body = {}) => {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, { method: "POST", headers: HJ(), body: JSON.stringify(body) });
  if (!r.ok) throw new Error((await r.text()) || fn);
  return r.json();
};

const count = (n) => (n == null ? null : Number(n).toLocaleString("en-US"));

// A LOCAL COPY of the sentence customer_shop() builds, not a read-back of it.
// platform_picks() does not return the disclosure — only the legal name it is built from —
// and a platform admin cannot call customer_shop() to fetch the real one (it opens with
// current_client_id() and raises not_a_customer). So this string is reproduced from
// sql/079 and can only drift if that function is reworded. The screen says so out loud
// rather than presenting a reconstruction as the published text.
const disclosureFor = (legal) =>
  `Paid links — ${legal}, the company behind this app, may earn a small commission if you buy. Prices are set by the retailer.`;

const loadError = (msg = "") =>
  /platform_admin_required/.test(msg) ? "This area is for the platform owner."
    : "Couldn't load the shelf.";

const saveError = (msg = "") =>
  /bad_url/.test(msg) ? "Affiliate links must start with https:// — the server refused this one. Nothing was saved."
    : /url_required/.test(msg) ? "A new pick needs its affiliate link. Nothing was saved."
    : /bad_retailer/.test(msg) ? "That isn't a retailer this shelf supports. Nothing was saved."
    : /not_found/.test(msg) ? "That pick is no longer there. Reload the page before editing anything else."
    : /platform_admin_required/.test(msg) ? "This area is for the platform owner."
    : /why_check|too long/.test(msg) ? "That line is longer than the 240 characters the shelf allows. Nothing was saved."
    : "Couldn't save that pick.";

export function PicksPage({ showToast }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // null · "load" (a refresh failed on its own) · "save" (the write landed, the read-back
  // did not). Those are opposite instructions to the operator, so they cannot share a flag.
  const [refreshErr, setRefreshErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [rowErr, setRowErr] = useState({});

  // Two loads can be in flight at once — a save triggers a reload, and a second save (or
  // a retry) can start before the first reload answers. Whichever request was issued last
  // is the only one allowed to write state, or an older snapshot lands on top of a newer
  // one and the row the operator just edited flips back to its previous value.
  const seq = useRef(0);
  // Whether a load has ever succeeded decides HOW a failure is shown: with nothing on
  // screen it is a dead page, with rows on screen it is stale data. Those are different
  // sentences, and a ref keeps them out of load()'s dependency list.
  const haveData = useRef(false);
  const savedTimer = useRef(null);
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  // Returns whether THIS caller got fresh rows on screen, so a save can tell the operator
  // the difference between "saved" and "saved but you are looking at the old list".
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const d = await rpc("platform_picks");
      if (mine !== seq.current) return true;
      haveData.current = true;
      setData(d);
      setErr(null);
      setRefreshErr(null);
      return true;
    } catch (e) {
      if (mine !== seq.current) return false;
      if (haveData.current) setRefreshErr("load");
      else setErr(loadError(e.message || ""));
      return false;
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Every save is followed by a full reload rather than a local merge: the RPC trims,
  // defaults and coerces (a blank title becomes the stored one, sort casts to int), so
  // the only way to show what is actually on the shelf is to read it back.
  const save = useCallback(async (id, patch) => {
    const key = id || "new";
    setBusyKey(key);
    setRowErr((m) => ({ ...m, [key]: null }));
    try {
      await rpc("platform_pick_upsert", { p_id: id, p_patch: patch });
      // The write is already committed at this point. A failed read-back after it is a
      // stale LIST, never an uncertain SAVE — saying "it may or may not have landed"
      // here would send the owner to re-check a pick that is already correct.
      const fresh = await load();
      if (!fresh) setRefreshErr("save");
      clearTimeout(savedTimer.current);
      setSavedKey(key);
      savedTimer.current = setTimeout(() => setSavedKey((s) => (s === key ? null : s)), 2500);
      const msg = fresh
        ? (id ? "Saved." : "Pick added.")
        : (id ? "Saved — but the list below couldn't be refreshed." : "Pick added — but the list below couldn't be refreshed.");
      showToast && showToast(msg, fresh ? "success" : "error");
      return true;
    } catch (e) {
      const m = saveError(e.message || "");
      setRowErr((r) => ({ ...r, [key]: m }));
      showToast && showToast(m, "error");
      return false;
    } finally {
      setBusyKey((b) => (b === key ? null : b));
    }
  }, [load, showToast]);

  const picks = useMemo(() => (data && Array.isArray(data.picks) ? data.picks : []), [data]);
  // customer_shop() runs the name through btrim before deciding whether a disclosure can
  // be written, so a name of spaces is the same as no name at all. Match that exactly —
  // a page that called this "set" while the app called it empty would be the worst of
  // both worlds.
  const legal = String((data && data.legal_name) || "").trim();
  const blocked = data != null && legal === "";

  const categories = useMemo(
    () => Array.from(new Set(picks.map((p) => p.category).filter(Boolean))).sort(),
    [picks],
  );

  // platform_picks() takes no arguments, so the filter and the search are local: there is
  // no second request for a stale one to win. The race guard that matters is on load().
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return picks.filter((p) => {
      if (filter === "on" && !p.active) return false;
      if (filter === "off" && p.active) return false;
      if (!q) return true;
      return [p.title, p.category, p.why, p.url, p.retailer]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q));
    });
  }, [picks, filter, search]);

  const onCount = useMemo(() => picks.filter((p) => p.active).length, [picks]);

  // An editor left open over a row the filter just removed sits under a list the pick is
  // no longer in and reads as belonging to whatever row took its place.
  useEffect(() => { setOpenId(null); }, [filter, search]);

  if (loading) return <div style={{ padding: 20, color: "var(--text2)" }}>Loading the shelf…</div>;

  if (err) {
    return (
      <div className="card" style={{ padding: 24 }}>
        <p style={{ fontSize: 15, fontWeight: 600 }}>{err}</p>
        <p style={{ fontSize: 13.5, color: "var(--text2)", marginTop: 6 }}>
          Nothing was read, so this is a failed load — not an empty shelf. Whatever picks exist are
          still there and still live.
        </p>
        <button className="btn btn-ghost" style={{ marginTop: 14 }}
                onClick={() => { setLoading(true); load(); }}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="calm-pane">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Worth a look</div>
          <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
            The platform's affiliate shelf. A retailer sells it, a retailer ships it — the operator's
            crew never touches anything on this list.
          </div>
        </div>
        <div className="seg">
          {[["all", "All"], ["on", "Switched on"], ["off", "Off"]].map(([k, l]) => (
            <button key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
      </div>

      {/* ── THE COMPLIANCE GATE ──────────────────────────────────────────────────
          Loud on purpose, and first on purpose. With no legal entity name the shelf is
          not a degraded shelf, it is an absent one — so this cannot read like a settings
          nag sitting above a working page. */}
      {blocked ? (
        <div role="alert" style={{
          border: "2px solid var(--danger, #c0392b)",
          background: "rgba(215,50,43,.08)",
          borderRadius: 14,
          padding: isMobile ? "14px 15px" : "18px 20px",
          marginBottom: 18,
        }}>
          <div style={{ fontSize: isMobile ? 16 : 19, fontWeight: 700, letterSpacing: "-.015em", color: "var(--danger, #c0392b)" }}>
            The shelf is not rendering. No customer can see any of this.
          </div>
          <p style={{ margin: "10px 0 0", fontSize: 14.5, lineHeight: 1.55 }}>
            “Worth a look” is absent from the customer app entirely — not empty, not greyed out, not
            there. The FTC's endorsement rules (16 CFR 255) require the disclosure to name the company
            that earns the commission, and the platform's legal entity name has not been set, so there
            is nothing to name. A disclosure that cannot name the company is not a disclosure, so the
            app renders no shelf at all rather than an undisclosed one.
          </p>
          <p style={{ margin: "10px 0 0", fontSize: 14.5, lineHeight: 1.55, fontWeight: 600 }}>
            Every pick below is invisible to every customer until the platform's legal entity name is
            set. Adding, editing and switching them on changes nothing anyone can see.
          </p>
          <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--text2)", lineHeight: 1.5 }}>
            It is <code>platform_legal_name</code> on the platform organization. This page only reads it
            back — no screen writes it yet, so it has to be set on the row directly. Setting it is
            necessary but not sufficient: sql/079 also requires each operator's own{" "}
            <code>allow_shop</code>, which is <code>NOT NULL DEFAULT false</code>, so the shelf stays
            absent for every operator who has not been switched on separately.
          </p>
        </div>
      ) : (
        // Never rendered with a placeholder name. A specimen disclosure naming nobody is
        // the exact thing 16 CFR 255 is about, and seeing one here would suggest the app
        // is printing something it is not.
        <div style={{
          border: "1px solid var(--border)",
          borderLeft: "3px solid var(--accent)",
          borderRadius: 12,
          background: "var(--surface)",
          padding: "14px 16px",
          marginBottom: 18,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--text2)" }}>
            The sentence the app prints above the shelf
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 15, lineHeight: 1.5 }}>{disclosureFor(legal)}</p>
          <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--text3)", lineHeight: 1.5 }}>
            Built from the legal entity name on file, {legal}. Wherever the shelf renders, this line is
            on screen with it — never behind a tap, a footer or Settings — and every card also carries
            “Paid link”.
          </div>
          <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--text3)", lineHeight: 1.5 }}>
            Reproduced from <code>sql/079</code>, not read back from it: <code>platform_picks()</code>{" "}
            returns the legal name, not the finished sentence. If that sentence is ever reworded in{" "}
            <code>customer_shop()</code>, change it here in the same commit or this preview quietly
            stops matching what is published.
          </div>
          <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--text3)", lineHeight: 1.5 }}>
            The name being set clears the platform-wide gate. Each operator still has to have{" "}
            <code>allow_shop</code> on — it defaults to false — and a pick is also withheld from any
            operator whose own crew already delivers that category. This page is not told which
            operators are switched on, so it does not guess.
          </div>
        </div>
      )}

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, minmax(0,1fr))",
        gap: 12,
        marginBottom: 16,
      }}>
        {/* "Switched on" is a platform-level fact this RPC actually returns. It is not a
            claim that a customer can see the pick — that also needs the operator's
            allow_shop, which platform_picks() says nothing about. */}
        <Stat label="Picks" value={count(picks.length)}
              sub={`${onCount} switched on here · ${picks.length - onCount} off${blocked ? " · none of them rendering anywhere" : ""}`} />
        <Stat label="Taps · 30 days" value={count(data && data.clicks_30d)}
              sub="every outbound tap on a pick" accent />
        {/* Attributed is a subset, not a rate, and it is not a measure of interest: the
            gap between the two is people who declined the attribution ask, whose taps are
            still counted but tied to nobody. Labelling it "conversions" or comparing the
            two as a percentage would misread the privacy design as a funnel. */}
        <Stat label="Tied to a customer · 30 days" value={count(data && data.attributed_30d)}
              sub="only the customers who opted in — the rest are counted, never linked to a person" />
      </div>

      {refreshErr ? (
        <div className="alert alert-error" style={{ marginBottom: 12 }}>
          {refreshErr === "save"
            ? "That edit was saved — the server accepted the write. What failed was reading the shelf back, so the list below is the state from before your edit. Reload the page before changing anything else."
            : "Couldn't reload the shelf from the server, so what's below may be out of date. Reload the page before changing anything else."}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input className="input" placeholder="Search title, category or link" value={search}
               onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320, flex: "1 1 200px" }} />
        <button className="btn btn-ghost" onClick={() => { setAdding((a) => !a); setOpenId(null); }}>
          {adding ? "Cancel" : "Add a pick"}
        </button>
      </div>

      {adding ? (
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>A new pick</div>
          <PickForm
            pick={null}
            categories={categories}
            isMobile={isMobile}
            busy={busyKey === "new"}
            saved={savedKey === "new"}
            error={rowErr.new}
            onCancel={() => setAdding(false)}
            onSave={async (id, patch) => { if (await save(id, patch)) setAdding(false); }}
          />
        </div>
      ) : null}

      {shown.length === 0 ? (
        <div className="card" style={{ padding: 28, textAlign: "center", color: "var(--text2)" }}>
          {picks.length === 0
            ? "No picks on the shelf yet. Add one and it joins the list here."
            : "Nothing matches that. Widen the filter or clear the search to see the rest."}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {shown.map((p) => {
            const isOpen = openId === p.id;
            const toggle = () => setOpenId(isOpen ? null : p.id);
            return (
              <div key={p.id} style={{ borderBottom: "1px solid var(--border)" }}>
                <div className="alist-row" style={{ alignItems: "flex-start", opacity: p.active ? 1 : 0.65 }}
                     role="button" tabIndex={0} aria-expanded={isOpen}
                     onClick={toggle}
                     onKeyDown={(e) => {
                       if (e.target !== e.currentTarget) return;      // the switch button handles its own keys
                       if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
                     }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.title}
                    </div>
                    <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
                      {[p.category, p.retailer, `sort ${p.sort}`].filter(Boolean).join(" · ")}
                    </div>
                    {p.why ? (
                      <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 4, lineHeight: 1.45 }}>“{p.why}”</div>
                    ) : null}
                    {/* A failed switch-off used to leave nothing behind on a collapsed row:
                        the message went into rowErr, which only the (closed) editor renders,
                        and to a toast the caller is not obliged to pass. The badge would still
                        read "On" and the owner would believe he had pulled the pick. */}
                    {!isOpen && rowErr[p.id] ? (
                      <div role="alert" style={{ fontSize: 12.5, color: "var(--danger, #c0392b)", marginTop: 5, fontWeight: 600 }}>
                        {rowErr[p.id]}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ display: "grid", gap: 6, justifyItems: "end", flexShrink: 0 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {p.retailer === "amazon" ? <span className="badge badge-gold">Amazon</span> : null}
                      {/* A green "On" while the shelf is not rendering would be the page
                          contradicting its own banner two inches lower. */}
                      <span className={`badge ${p.active && !blocked ? "badge-green" : "badge-gray"}`}>
                        {p.active ? (blocked ? "On · not rendering" : "On") : "Off"}
                      </span>
                    </div>
                    <span style={{ fontSize: 11.5, color: "var(--text3)" }}>
                      {count(p.clicks_30d)} tap{Number(p.clicks_30d) === 1 ? "" : "s"} · 30d
                    </span>
                    {/* The row itself opens the editor, so this button has to stop the
                        click or switching a pick off would also expand it. */}
                    <button className="btn btn-xs btn-ghost" disabled={busyKey === p.id}
                            onClick={(e) => { e.stopPropagation(); save(p.id, { active: !p.active }); }}>
                      {busyKey === p.id ? "Saving…" : p.active ? "Switch off" : "Switch on"}
                    </button>
                    {!isOpen && savedKey === p.id ? (
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ok, #1a7f37)" }}>Saved</span>
                    ) : null}
                  </div>
                </div>

                {isOpen ? (
                  <div style={{ padding: "0 8px 20px" }}>
                    <PickForm
                      pick={p}
                      categories={categories}
                      isMobile={isMobile}
                      busy={busyKey === p.id}
                      saved={savedKey === p.id}
                      error={rowErr[p.id]}
                      onCancel={() => setOpenId(null)}
                      onSave={save}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const blankDraft = { retailer: "chewy", category: "", title: "", why: "", url: "", sort: "100", active: true };
const draftFrom = (p) => (p ? {
  retailer: p.retailer || "chewy",
  category: p.category || "",
  title: p.title || "",
  why: p.why || "",
  url: p.url || "",
  sort: p.sort == null ? "" : String(p.sort),
  active: !!p.active,
} : { ...blankDraft });

// The editor for one pick, and for the new one. Both go through platform_pick_upsert, so
// both live here — a separate "add" form would drift out of step with the validation the
// server actually applies.
//
// There is no price input and there will not be one; see the note at the top of the file.
function PickForm({ pick, categories, isMobile, busy, saved, error, onSave, onCancel }) {
  const [d, setD] = useState(() => draftFrom(pick));
  const [warn, setWarn] = useState(null);

  // Reset the draft when THIS row changed on the server, or when this row's own save just
  // landed — not merely because a new object arrived.
  //
  // Every save reloads the whole list, which hands every open editor a brand-new object.
  // Resetting on object identity therefore wiped an operator's half-typed URL the moment
  // he switched a DIFFERENT pick off, with no warning and nothing to recover. Comparing
  // the stored values keeps the intended behaviour — after his own save he is looking at
  // what the database holds, trimmed and coerced — without letting an unrelated reload
  // throw away work in progress.
  const storedRef = useRef(pick ? JSON.stringify(draftFrom(pick)) : null);
  const savedRef = useRef(false);
  useEffect(() => {
    const next = pick ? JSON.stringify(draftFrom(pick)) : null;
    const rowChanged = next !== storedRef.current;
    const justSaved = !!saved && !savedRef.current;
    savedRef.current = !!saved;
    if (!rowChanged && !justSaved) return;
    storedRef.current = next;
    setD(draftFrom(pick));
    setWarn(null);
  }, [pick, saved]);

  const set = (k) => (e) => {
    const v = k === "active" ? e.target.value === "1" : e.target.value;
    setD((c) => ({ ...c, [k]: v }));
    if (warn) setWarn(null);
  };
  const listId = pick ? `pick-cats-${pick.id}` : "pick-cats-new";

  function submit() {
    const title = d.title.trim();
    const category = d.category.trim();
    const url = d.url.trim();
    const why = d.why.trim();
    const rawSort = String(d.sort).trim();

    // platform_pick_upsert COALESCEs a blank title or category back to what is already
    // stored (or to 'Untitled' / 'other' on a create) and answers 200. So an emptied field
    // would be reported as saved while the shelf kept the old text — refuse it here
    // instead of letting the page claim something that did not happen.
    if (!title) { setWarn("A pick needs a title."); return; }
    if (!category) {
      setWarn("A pick needs a category. It is what keeps this pick off the shelf for operators whose own crew already delivers that category.");
      return;
    }
    if (!url) { setWarn(pick ? "A pick needs its affiliate link." : "A new pick needs its affiliate link."); return; }
    // The server's check is the case-sensitive regex ^https:// , so HTTPS:// is refused
    // there too. Matching it exactly keeps the message here honest.
    if (!/^https:\/\//.test(url)) { setWarn("Affiliate links must start with https:// — the server refuses anything else."); return; }
    if (why.length > 240) { setWarn("Keep the line to 240 characters or the shelf rejects it."); return; }

    const sort = rawSort === "" ? null : Number(rawSort);
    if (sort !== null && !Number.isInteger(sort)) { setWarn("Sort has to be a whole number."); return; }

    if (!pick) {
      onSave(null, {
        retailer: d.retailer, category, title, why, url, active: d.active,
        ...(sort === null ? {} : { sort }),
      });
      return;
    }

    // Only what actually changed. `why` is the one field the RPC will clear on an empty
    // string; the rest treat blank as "leave it alone", so sending them unchanged would be
    // noise at best and a no-op the operator reads as a save at worst.
    const patch = {};
    if (d.retailer !== pick.retailer) patch.retailer = d.retailer;
    if (category !== pick.category) patch.category = category;
    if (title !== pick.title) patch.title = title;
    if (why !== (pick.why || "")) patch.why = why;
    if (url !== pick.url) patch.url = url;
    if (sort !== null && sort !== pick.sort) patch.sort = sort;
    if (d.active !== pick.active) patch.active = d.active;
    if (Object.keys(patch).length === 0) { setWarn("Nothing changed."); return; }
    onSave(pick.id, patch);
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <Field label="Title">
        <input className="input" value={d.title} onChange={set("title")} maxLength={120}
               style={{ width: "100%", marginTop: 3 }} />
      </Field>

      <Field label="Why this one" hint={`One line, in your own voice. Never a review, never a rating. ${d.why.length}/240`}>
        <textarea className="input" value={d.why} onChange={set("why")} maxLength={240} rows={2}
                  style={{ width: "100%", marginTop: 3 }} />
      </Field>

      <Field label="Link" hint="The customer never receives this URL. Each tap gets its own logged copy, so an outbound click is attributed exactly once.">
        <input className="input" type="url" value={d.url} onChange={set("url")} placeholder="https://"
               style={{ width: "100%", marginTop: 3 }} />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(190px,1fr))", gap: 10 }}>
        <Field label="Category" hint="Matched exactly. An operator whose crew delivers this category never sees this pick.">
          <input className="input" value={d.category} onChange={set("category")} list={listId}
                 style={{ width: "100%", marginTop: 3 }} />
          <datalist id={listId}>
            {categories.map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>

        <Field label="Retailer">
          <select className="input" value={d.retailer} onChange={set("retailer")} style={{ width: "100%", marginTop: 3 }}>
            <option value="chewy">Chewy — the only retailer live in v1</option>
            <option value="amazon">Amazon — hidden for every operator by default</option>
          </select>
        </Field>

        <Field label="Sort" hint="Lower comes first.">
          <input className="input" type="number" step="1" value={d.sort} onChange={set("sort")}
                 style={{ width: "100%", marginTop: 3 }} />
        </Field>

        <Field label="On the shelf">
          <select className="input" value={d.active ? "1" : "0"} onChange={set("active")} style={{ width: "100%", marginTop: 3 }}>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        </Field>
      </div>

      {d.retailer === "amazon" ? (
        <div className="alert alert-info" style={{ margin: 0 }}>
          An Amazon pick stays hidden from an operator's customers until that operator's{" "}
          <code>allow_amazon</code> is switched on, and it is off for everyone by default — saving this
          publishes it to nobody until then. When it is on, the link has to open in Safari or the Amazon
          app: opening an Amazon link inside an in-app web view breaks the Associates terms. Chewy is the
          only retailer live in v1.
        </div>
      ) : null}

      <div style={{ fontSize: 12, color: "var(--text3)" }}>
        No price, on purpose — the retailer sets it and changes it without telling us.
      </div>

      {warn ? <div role="alert" style={{ fontSize: 13, color: "var(--danger, #c0392b)" }}>{warn}</div> : null}
      {error ? <div role="alert" style={{ fontSize: 13, color: "var(--danger, #c0392b)" }}>{error}</div> : null}

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn btn-primary" disabled={busy} onClick={submit}>
          {busy ? "Saving…" : pick ? "Save" : "Add this pick"}
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={onCancel}>Cancel</button>
        {saved ? <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ok, #1a7f37)" }}>Saved</span> : null}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ fontSize: 12, color: "var(--text2)" }}>{label}</span>
      {children}
      {hint ? <span style={{ display: "block", fontSize: 11.5, color: "var(--text3)", marginTop: 3, lineHeight: 1.45 }}>{hint}</span> : null}
    </label>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={accent ? { color: "var(--accent)" } : undefined}>{value ?? "—"}</div>
      {sub ? <div className="stat-sub">{sub}</div> : null}
    </div>
  );
}
